import React, { useCallback, useEffect, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { QRCodeCanvas } from "qrcode.react";
import { downloadFile } from "../../../shared/utils/fileUtils";
import { Machine } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import usePermission from "../../../shared/hooks/usePermission";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { Modules } from "../../../core/models/PermissionModels";
import { fetchNameplateStatus } from "../customerNameplatesSlice";
import { dt } from "../utils/dt";
import { FormActionBar } from "../../../shared/components/redesign/forms";
import Loader from "../../../shared/components/Loader";
import MachineQRCodeDialogIdentity from "./MachineQRCodeDialogIdentity";
import MachineQRCodeDialogNotice from "./MachineQRCodeDialogNotice";

export interface MachineQRCodeDialogProps {
  open: boolean;
  machine: Machine;
  onClose?: () => void;
}

const QR_CANVAS_ID = "machine-nameplate-qr";

/** Quiet zone in QR modules. The spec requires 4; without it scanners fail. */
const QR_MARGIN_MODULES = 4;

const MachineQRCodeDialog = ({ open, machine, onClose }: MachineQRCodeDialogProps) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const { isPhone } = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  useRedesignSetup();

  /**
   * B7-FE1 cog (plan §6A.10). Deep-links to the digital-nameplate control panel
   * with THIS machine pre-selected, so a plate-handling owner does not have to
   * re-find the machine they are already looking at.
   *
   * Gated on the Settings module: the whole /settings tree sits behind
   * PermissionRouteGuard(Modules.Settings) and Managers hold Settings: 0 (owner
   * ruling R1-A), so an ungated cog would be a dead link for them. A machine GUID
   * in an authenticated route is fine — the no-GUID rule covers the ANONYMOUS
   * nameplate wire only.
   */
  const { hasAccess: canOpenSettings } = usePermission(Modules.Settings);

  const handleOpenNameplateSettings = () => {
    navigate(`/settings/nameplates/${machine.id}`);
    onClose?.();
  };

  /**
   * D7-FE1 — ONE code per machine, plate first (owner ruling D7, 2026-08-11:
   * `B7_D7_D8_ONE_CODE_DEFAULT_ON_PLAN.md` §3).
   *
   * This dialog USED to encode the kiosk URL /public-v2/machines/{internalNumber}
   * while the Settings panel encoded the nameplate id-link — two codes per
   * machine, and whoever printed a sticker had to know which was which. It now
   * encodes the SAME id-link the panel does: scanning lands on the public digital
   * nameplate, and the plate's own affordances escalate into the kiosk for people
   * who have access.
   *
   * GUARD — do not "fix" this back:
   *  • The link is consumed VERBATIM from the server (plan §4). Never assemble
   *    `https://{host}/m/{code}` on the client — the resolver host is deployed
   *    config, and a client-built link would silently mint dead stickers.
   *  • Never fall back to the kiosk URL when the plate is off or the fetch fails.
   *    A wrong-but-working QR is the bug this task killed: it gets printed and
   *    glued to a machine, and it opens a sign-in screen instead of the plate.
   *  • Already-printed kiosk stickers keep working — no route changed (§3.4).
   *
   * The status GET is `Machinery: Write` (SEC-REQ-B7-12) and a Read-level link
   * endpoint was REJECTED by Security, so every entry point into this dialog is
   * wrapped in PermissionGuard(Machinery, Write) — SEC-REQ-B7-34 option (i).
   * Ungating an entry point would hand Workers a guaranteed 403 and an empty
   * dialog. Refetch on every open: the plate may have been switched from the
   * Settings panel since the last time this dialog was shown.
   */
  const status = useAppSelector((s) => s.customerNameplates.statuses[machine.id]);
  const statusLoading = useAppSelector((s) => s.customerNameplates.statusLoading[machine.id]);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadStatus = useCallback(() => {
    setLoadFailed(false);
    dispatch(fetchNameplateStatus(machine.id))
      .unwrap()
      .catch(() => setLoadFailed(true));
  }, [dispatch, machine.id]);

  useEffect(() => {
    if (!open) {
      return;
    }
    loadStatus();
  }, [open, loadStatus]);

  const idLink = status?.idLink || "";
  const plateReady = !!status?.enabled && !!idLink;
  const showLoader = !loadFailed && !!statusLoading && !status;

  const qrSize = isPhone ? 240 : 340;

  const handleDownload = () => {
    const qrCanvas = document.getElementById(QR_CANVAS_ID) as HTMLCanvasElement | null;
    if (!qrCanvas) {
      return;
    }

    const qrCodeUrl = qrCanvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
    // Same filename the Settings panel produces — one code, one file name.
    downloadFile(qrCodeUrl, `nameplate-${machine.internalNumber}-qr.png`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : dt.radius,
          fontFamily: dt.font,
          overflow: "hidden",
        },
      }}
    >
      {/* Title bar with accent left rail */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontFamily: dt.font,
          fontSize: "16px",
          fontWeight: 700,
          color: dt.textPrimary,
          borderBottom: `1px solid ${dt.cardBorder}`,
          borderLeft: `4px solid ${dt.blue}`,
          py: 2,
          px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <QrCode2Icon sx={{ fontSize: 20, color: dt.blue, flexShrink: 0 }} />
          <Box
            component="span"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {translate("qrCode")}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {canOpenSettings && (
            <Tooltip title={translate("nameplateSettings")}>
              <IconButton
                aria-label={translate("nameplateSettings")}
                onClick={handleOpenNameplateSettings}
                size="small"
                sx={{ color: dt.textMuted }}
              >
                <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          <IconButton
            aria-label={translate("common:buttons.close")}
            onClick={onClose}
            size="small"
            sx={{ color: dt.textMuted }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 2, overflowX: "hidden" }}>
        {/* Machine identification — so the right code is on screen */}
        <MachineQRCodeDialogIdentity machine={machine} />

        {/* STATE 3 — the status fetch failed. Plain retry; deliberately NO QR:
            we do not know what this machine's code is, and guessing prints a
            dead sticker. */}
        {loadFailed && (
          <MachineQRCodeDialogNotice
            variant="error"
            body={translate("plateLoadFailed")}
            actionLabel={translate("plateRetry")}
            onAction={loadStatus}
          />
        )}

        {/* Transient — first fetch in flight. */}
        {showLoader && (
          <Box sx={{ mt: 3, py: 4, display: "flex", justifyContent: "center" }}>
            <Loader />
          </Box>
        )}

        {/* STATE 2 — plate switched off, or no code minted yet. Guidance, not an
            error: most of the fleet is default-OFF until D8 ships. The cog is the
            fix, so the CTA is the same deep link — shown only to users who can
            actually enter Settings (ruling R1-A). */}
        {!loadFailed && !showLoader && !plateReady && (
          <MachineQRCodeDialogNotice
            variant="plateOff"
            title={translate("plateOffTitle")}
            body={translate("plateOffBody")}
            hint={canOpenSettings ? undefined : translate("plateOffNoAccess")}
            actionLabel={canOpenSettings ? translate("plateOffCta") : undefined}
            onAction={canOpenSettings ? handleOpenNameplateSettings : undefined}
          />
        )}

        {/* STATE 1 — the plate QR: the machine's single code. `idLink` is encoded
            verbatim exactly as the server returned it (see the guard note above). */}
        {!loadFailed && !showLoader && plateReady && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 3,
            }}
          >
            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                border: `1px solid ${dt.cardBorder}`,
                borderRadius: dt.radius,
                boxShadow: dt.cardShadow,
                p: isPhone ? 1.5 : 2,
                lineHeight: 0,
                maxWidth: "100%",
                animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
              }}
            >
              <QRCodeCanvas
                id={QR_CANVAS_ID}
                value={idLink}
                size={qrSize}
                level="M"
                marginSize={QR_MARGIN_MODULES}
                bgColor="#FFFFFF"
                fgColor="#000000"
                style={{ display: "block", maxWidth: "100%", height: "auto" }}
              />
            </Box>

            <Box
              sx={{
                mt: 2,
                maxWidth: 300,
                textAlign: "center",
                fontFamily: dt.font,
                fontSize: "13px",
                lineHeight: 1.5,
                color: dt.textSecondary,
              }}
            >
              {translate("plateQrHint")}
            </Box>
          </Box>
        )}

        <FormActionBar
          onCancel={onClose ?? (() => undefined)}
          onSubmit={handleDownload}
          submitLabel={translate("common:buttons.download")}
          cancelLabel={translate("common:buttons.close")}
          submitIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
          submitDisabled={!plateReady}
          variant="pill"
          animation={false}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MachineQRCodeDialog;
